import React from "react";
import {
  Box,
  Chip,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import brokerLogo from "../../assests/images/brokerlogo.png";
import { NavLink, useLocation } from "react-router-dom";
import SidebarThemeSwitcher from "./SidebarThemeSwitcher";
import { SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED } from "./sidebarConstants";
import { useNotifications } from "../../context/NotificationContext";

const fontSans = '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const items = [
  { id: "dashboard", label: "Dashboard", icon: DashboardOutlinedIcon, to: "/dashboard/home" },
  {
    id: "customers",
    label: "Customers",
    icon: GroupOutlinedIcon,
    to: "/dashboard/customers/clients",
    children: [
      { id: "customers-clients", label: "Clients", to: "/dashboard/customers/clients" },
      { id: "customers-user-approvals", label: "User Approvals", to: "/dashboard/customers/user-approvals" },
    ],
  },
  {
    id: "announcements",
    label: "Announcements",
    icon: CampaignOutlinedIcon,
    to: "/dashboard/announcements/approval",
    children: [
      { id: "announcements-approval", label: "Approval queue", to: "/dashboard/announcements/approval" },
      { id: "announcements-active", label: "Active listings", to: "/dashboard/announcements/active" },
      // { id: "announcements-guest", label: "Guest preview", to: "/dashboard/announcements/guest" },
    ],
  },
  { id: "items", label: "Items", icon: SettingsOutlinedIcon, to: "/dashboard/items" },
];

function resolveActiveGroup(pathname) {
  if (pathname.includes("/customers")) return "customers";
  if (pathname.includes("/announcements")) return "announcements";
  if (pathname.includes("/items")) return "items";
  return "dashboard";
}

function IconPill({ Icon, active, s, collapsed }) {
  return (
    <Box
      sx={{
        width: collapsed ? 40 : 36,
        height: collapsed ? 40 : 36,
        borderRadius: 2,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
        bgcolor: active ? s.iconPillActiveBg : s.iconPillBg,
        border: `1px solid ${active ? s.iconPillActiveBorder : s.iconPillBorder}`,
        boxShadow: active ? "0 0 20px rgba(212, 168, 74, 0.2)" : "none",
        "& .MuiSvgIcon-root": {
          fontSize: collapsed ? 22 : 20,
          color: active ? s.itemIconActive : s.itemIcon,
          transition: "color 0.2s ease",
        },
      }}
    >
      <Icon />
    </Box>
  );
}

function NavChild({ child, s, onNavClick, location, badgeLabel }) {
  const displayBadge = badgeLabel || child.badge;
  const childActive = location.pathname === child.to;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 0.75,
        mb: 0.35,
        "&:last-of-type": { mb: 0 },
      }}
    >
      <Typography
        component={NavLink}
        to={child.to}
        onClick={() => onNavClick?.()}
        sx={{
          fontFamily: fontSans,
          flex: 1,
          py: 0.7,
          px: 1.1,
          borderRadius: 2,
          fontSize: 12.5,
          fontWeight: childActive ? 600 : 500,
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          gap: 0.85,
          lineHeight: 1.35,
          color: childActive ? s.childTextActive : s.childText,
          bgcolor: childActive ? "rgba(212, 168, 74, 0.18)" : "transparent",
          border: `1px solid ${childActive ? s.accentBorder : "transparent"}`,
          transition: "all 0.18s ease",
          "&:hover": {
            bgcolor: childActive ? "rgba(212, 168, 74, 0.26)" : s.hoverBg,
            color: s.childTextActive,
            transform: "translateX(2px)",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            bgcolor: childActive ? s.accent : "rgba(255,255,255,0.25)",
            flexShrink: 0,
            boxShadow: childActive ? `0 0 8px ${s.accent}` : "none",
          }}
        />
        {child.label}
      </Typography>
      {displayBadge ? (
        <Chip
          label={displayBadge}
          size="small"
          sx={{
            height: 20,
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: "0.02em",
            bgcolor: "#e85555",
            color: "#fff",
            borderRadius: "6px",
            boxShadow: "0 2px 10px rgba(240,85,95,0.4)",
            "& .MuiChip-label": { px: 0.75, fontFamily: fontSans },
          }}
        />
      ) : null}
    </Box>
  );
}

function NavItem({ item, collapsed, s, activeGroup, location, onNavClick, userApprovalUnreadCount = 0 }) {
  const isActive = activeGroup === item.id;
  const hasChildren = Boolean(item.children?.length);
  const showChildren = !collapsed && hasChildren && isActive;
  const Icon = item.icon;

  const button = (
    <ListItemButton
      component={NavLink}
      to={item.to}
      onClick={() => onNavClick?.()}
      sx={{
        mb: 0.5,
        borderRadius: 2.5,
        minHeight: collapsed ? 48 : 46,
        px: collapsed ? 0.75 : 1,
        py: 0.75,
        justifyContent: collapsed ? "center" : "flex-start",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
        border: `1px solid ${isActive ? s.accentBorder : "transparent"}`,
        bgcolor: isActive ? s.activeBg : "transparent",
        boxShadow: isActive ? "0 2px 12px rgba(0,0,0,0.15)" : "none",
        "&::before": isActive
          ? {
              content: '""',
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              width: 3,
              height: "56%",
              borderRadius: "0 4px 4px 0",
              bgcolor: s.accent,
              boxShadow: `0 0 8px ${s.accent}`,
            }
          : undefined,
        "&:hover": {
          bgcolor: isActive ? s.activeHoverBg : s.hoverBg,
          borderColor: isActive ? s.accentBorder : "rgba(255,255,255,0.08)",
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 0 : 1.1, justifyContent: "center" }}>
        <IconPill Icon={Icon} active={isActive} s={s} collapsed={collapsed} />
      </ListItemIcon>
      {!collapsed ? (
        <>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontFamily: fontSans,
              fontSize: 13.5,
              fontWeight: isActive ? 600 : 500,
              letterSpacing: "0.01em",
              color: isActive ? s.itemTextActive : s.itemText,
              lineHeight: 1.25,
              noWrap: true,
            }}
          />
          {hasChildren ? (
            <ChevronRightOutlinedIcon
              sx={{
                fontSize: 18,
                color: isActive ? s.itemIconActive : "rgba(158,166,178,0.55)",
                transform: showChildren ? "rotate(90deg)" : "none",
                transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                flexShrink: 0,
              }}
            />
          ) : null}
        </>
      ) : null}
    </ListItemButton>
  );

  return (
    <Box key={item.id}>
      {collapsed ? (
        <Tooltip title={item.label} placement="right" arrow>
          {button}
        </Tooltip>
      ) : (
        button
      )}
      {hasChildren ? (
        <Collapse in={showChildren} timeout={280} unmountOnExit>
          <Box
            sx={{
              ml: 1.5,
              mr: 0.5,
              mb: 1,
              mt: 0.25,
              pl: 1.5,
              py: 0.75,
              borderLeft: `2px solid ${s.accentBorder}`,
              borderRadius: "0 14px 14px 0",
              bgcolor: s.childPanelBg,
              backdropFilter: "blur(8px)",
            }}
          >
            {item.children.map((child) => (
              <NavChild
                key={child.id}
                child={child}
                s={s}
                onNavClick={onNavClick}
                location={location}
                badgeLabel={
                  child.id === "customers-user-approvals" && userApprovalUnreadCount > 0
                    ? `${userApprovalUnreadCount} New`
                    : undefined
                }
              />
            ))}
          </Box>
        </Collapse>
      ) : null}
    </Box>
  );
}

export default function AdminSidebar({ collapsed, onNavClick }) {
  const location = useLocation();
  const theme = useTheme();
  const s = theme.admin?.sidebar || {};
  const { userApprovalUnreadCount } = useNotifications();
  const activeGroup = resolveActiveGroup(location.pathname);
  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;

  return (
    <Box
      sx={{
        position: "relative",
        width,
        transition: "width 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: fontSans,
        background: s.background,
        borderRight: `1px solid ${s.border}`,
        boxShadow: "8px 0 32px rgba(0,0,0,0.12)",
        overflow: "hidden",
      }}
    >
      {/* Brand header */}
      <Box
        sx={{
          flexShrink: 0,
          position: "relative",
          zIndex: 1,
          px: collapsed ? 1 : 1.75,
          py: collapsed ? 2 : 2.25,
          borderBottom: `1px solid ${s.divider}`,
        }}
      >
        <StackBrand collapsed={collapsed} s={s} />
      </Box>

      {/* Nav */}
      <List
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          px: collapsed ? 0.75 : 1.25,
          py: 1.5,
          position: "relative",
          zIndex: 1,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            background: s.scrollThumb,
            borderRadius: 4,
          },
        }}
      >
        {!collapsed ? (
          <Typography
            sx={{
              px: 1,
              mb: 1,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: s.sectionLabel,
              fontFamily: fontSans,
            }}
          >
            Main menu
          </Typography>
        ) : null}
        {items.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            collapsed={collapsed}
            s={s}
            activeGroup={activeGroup}
            location={location}
            onNavClick={onNavClick}
            userApprovalUnreadCount={userApprovalUnreadCount}
          />
        ))}
      </List>

      <SidebarThemeSwitcher collapsed={collapsed} />
    </Box>
  );
}

function StackBrand({ collapsed, s }) {
  if (collapsed) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          component="img"
          src={brokerLogo}
          alt="Brokkerspot"
          sx={{
            width: 44,
            height: 44,
            objectFit: "contain",
            filter: s.logoShadow,
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          bgcolor: s.iconPillBg,
          border: `1px solid ${s.iconPillBorder}`,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={brokerLogo}
          alt="Brokkerspot"
          sx={{ width: 36, height: 36, objectFit: "contain", filter: s.logoShadow }}
        />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: fontSans,
            fontSize: 15,
            fontWeight: 700,
            color: s.itemTextActive,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          Brokkerspot
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            mt: 0.35,
            px: 0.75,
            py: 0.15,
            borderRadius: 1,
            bgcolor: "rgba(212, 168, 74, 0.15)",
            border: `1px solid ${s.accentBorder}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: fontSans,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: s.goldMuted,
            }}
          >
            Admin
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
